/**
 * PDP Photo Gallery — responsive Swiper behavior for PIM-driven product images.
 */
import Swiper from 'swiper';
import { A11y, Keyboard, Navigation, Pagination } from 'swiper/modules';
import { formatCarouselLabel } from './utils/lvzCarouselA11y';

(function initLvzProductPhotoGallery() {
	/**
	 * @param {Element|null} liveEl
	 * @param {number} index1
	 * @param {number} total
	 */
	function updateLive(liveEl, pattern, index1, total) {
		if (!liveEl) {
			return;
		}

		liveEl.textContent = formatCarouselLabel(pattern, index1, total);
	}

	/**
	 * @param {Element} section
	 */
	function initOne(section) {
		if (section.getAttribute('data-lvz-product-photo-gallery-init') === 'true') {
			return;
		}

		const swiperEl = section.querySelector('[data-lvz-product-photo-gallery-swiper]');
		if (!swiperEl) {
			return;
		}

		const slideCount = Number.parseInt(section.getAttribute('data-slide-count'), 10);
		if (Number.isNaN(slideCount) || slideCount < 2) {
			return;
		}

		const desktopCarousel = section.getAttribute('data-desktop-carousel') === 'true';
		const mobileCarousel = section.getAttribute('data-mobile-carousel') === 'true';
		const paginationEl = swiperEl.querySelector('.lvz-product-photo-gallery__pagination');
		const prevEl = swiperEl.querySelector('.swiper-button-prev');
		const nextEl = swiperEl.querySelector('.swiper-button-next');
		const liveEl = section.querySelector('.lvz-product-photo-gallery__live');
		const liveLabelPattern = section.getAttribute('data-live-label');
		const isSearchResults = section.closest('.product-tile__photo-gallery-wrapper') !== null;
		const isSubscriptionModal = section.closest('.subscription-learn-more-modal') !== null;

		/** @type {Record<string, unknown>} */
		const config = {
			modules: [Navigation, Pagination, A11y, Keyboard],
			slidesPerView: 'auto',
			spaceBetween: isSearchResults && !isSubscriptionModal ? 8 : 16,
			slidesOffsetBefore: isSearchResults ? 0 : 24,
			slidesOffsetAfter: isSearchResults ? 0 : 24,
			watchOverflow: true,
			grabCursor: mobileCarousel,
			keyboard: {
				enabled: true,
			},
			a11y: {
				enabled: true,
			},
			on: {
				init: function onInit(swiper) {
					updateLive(liveEl, liveLabelPattern, swiper.activeIndex + 1, slideCount);
				},
				slideChange: function onSlideChange(swiper) {
					updateLive(liveEl, liveLabelPattern, swiper.activeIndex + 1, slideCount);
				},
			},
			breakpoints: {
				1024: {
					spaceBetween: isSearchResults ? 16 : 24,
					slidesOffsetBefore: isSearchResults ? 0 : 80,
					slidesOffsetAfter: isSearchResults ? 0 : 80,
					allowTouchMove: desktopCarousel,
					centerInsufficientSlides: desktopCarousel,
					grabCursor: desktopCarousel,
				},
			},
		};

		if (paginationEl) {
			config.pagination = {
				el: paginationEl,
				clickable: true,
				enabled: mobileCarousel,
			};
			config.breakpoints[1024].pagination = {
				el: paginationEl,
				clickable: true,
				enabled: desktopCarousel,
			};
		}

		if (prevEl && nextEl) {
			config.navigation = {
				prevEl: prevEl,
				nextEl: nextEl,
				enabled: false,
				addIcons: false,
			};
			config.breakpoints[1024].navigation = {
				prevEl: prevEl,
				nextEl: nextEl,
				enabled: desktopCarousel,
				addIcons: false,
			};
		}

		const swiper = new Swiper(swiperEl, config);
		section._lvzProductPhotoGallerySwiper = swiper;
		section.setAttribute('data-lvz-product-photo-gallery-init', 'true');

		requestAnimationFrame(function () {
			swiper.update();
		});
	}

	function boot() {
		document.querySelectorAll('[data-lvz-product-photo-gallery]').forEach(function (section) {
			initOne(section);
		});
	}

	function initIn(root, options) {
		const scope = root || document;
		const force = options && options.force;

		scope.querySelectorAll('[data-lvz-product-photo-gallery]').forEach(function (section) {
			const existing = section._lvzProductPhotoGallerySwiper;

			if (existing && force) {
				try {
					existing.destroy(true, true);
				} catch (err) {
					// ignore destroy errors after DOM moves
				}
				section._lvzProductPhotoGallerySwiper = null;
				section.removeAttribute('data-lvz-product-photo-gallery-init');
			}

			initOne(section);

			if (section._lvzProductPhotoGallerySwiper) {
				requestAnimationFrame(function () {
					section._lvzProductPhotoGallerySwiper.update();
				});
			}
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', boot);
	} else {
		boot();
	}

	window.lvzProductPhotoGallery = {
		initIn: initIn,
	};
})();
